/**
 * MechaGuard - SkillBar (技能栏)
 *
 * 显示能量点数与 3 个特殊技能按钮。
 *
 * 性能约定：SkillSystem 在还有技能冷却时每帧都会通知，
 * 因此这里的 sync() 必须自己做去重 —— 冷却数字按「整数秒」缓存，
 * 冷却填充按「整数百分比」缓存，避免每帧重排 3 个按钮。
 */

import { SKILLS } from '../systems/SkillSystem.js';

export class SkillBar {
  constructor() {
    /** @type {import('../systems/SkillSystem.js').SkillSystem|null} */
    this.system = null;
    this.pendingSkillId = null;

    this._root = null;
    this._els = {};
    /** @type {Map<string, HTMLButtonElement>} */
    this._buttons = new Map();
    /** @type {((id:string)=>void)|null} */
    this._onSelect = null;

    this._cache = { energy: null };
    /** id -> 整数秒（用于冷却数字去重） */
    this._cdSecCache = new Map();
    /** id -> 整数百分比（用于冷却填充去重） */
    this._cdPctCache = new Map();
    /** id -> 状态位组合（用于 classList 去重） */
    this._stateCache = new Map();
  }

  /**
   * @param {import('../systems/SkillSystem.js').SkillSystem} system
   * @param {(id:string)=>void} onSelect
   */
  init(system, onSelect) {
    this.system = system;
    this._onSelect = onSelect;

    const root = document.getElementById('skill-bar');
    if (!root) return;

    this._root = root;
    root.innerHTML = '';
    this._buttons.clear();

    // ---------- 能量区 ----------
    const energyBox = document.createElement('div');
    energyBox.className = 'energy-box';

    const energyIcon = document.createElement('span');
    energyIcon.className = 'energy-icon';
    energyIcon.textContent = '⚡';

    const pips = document.createElement('div');
    pips.className = 'energy-pips';

    const energyText = document.createElement('span');
    energyText.className = 'energy-text';

    energyBox.append(energyIcon, pips, energyText);

    // ---------- 技能按钮 ----------
    const buttons = document.createElement('div');
    buttons.className = 'skill-buttons';

    for (const skill of SKILLS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'skill-btn';
      btn.dataset.id = skill.id;
      btn.title = `${skill.name} · ${skill.desc}（消耗 ${skill.cost} 能量，冷却 ${skill.cooldown} 秒）`;

      // 冷却填充条（绝对定位，位于文字下层）
      const fill = document.createElement('span');
      fill.className = 'skill-cd-fill';

      const icon = document.createElement('span');
      icon.className = 'skill-icon';
      icon.textContent = skill.icon;

      const name = document.createElement('span');
      name.className = 'skill-name';
      name.textContent = skill.name.replace(/\s+/g, '');

      const cost = document.createElement('span');
      cost.className = 'skill-cost';
      cost.textContent = `${skill.cost}⚡`;

      const cd = document.createElement('span');
      cd.className = 'skill-cd';

      btn.append(fill, icon, name, cost, cd);

      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (this._onSelect) this._onSelect(skill.id);
      });

      buttons.appendChild(btn);
      this._buttons.set(skill.id, btn);
    }

    root.append(energyBox, buttons);

    this._els = { pips, energyText };
    this._cache.energy = null;

    this._renderEnergy();
    this.sync();
  }

  /** 设置当前处于瞄准状态的技能（null 表示无） */
  setPending(id) {
    if (this.pendingSkillId === id) return;
    this.pendingSkillId = id;
    this.sync();
  }

  /**
   * 与 SkillSystem 同步。
   * 由 Game 的 onEnergyChange / onSkillStateChange 钩子调用，可能是每帧。
   */
  sync() {
    const sys = this.system;
    if (!sys || !this._root) return;

    // ---- 能量（只在数值变化时重建 pip） ----
    if (sys.energy !== this._cache.energy) {
      this._cache.energy = sys.energy;
      this._renderEnergy();
    }

    // ---- 各技能按钮 ----
    for (const skill of SKILLS) {
      const btn = this._buttons.get(skill.id);
      if (!btn) continue;

      const cd = sys.cooldownOf(skill.id);
      const affordable = sys.energy >= skill.cost;
      const isPending = this.pendingSkillId === skill.id;

      // 状态类名切换很便宜，但也做一次去重
      const stateKey = `${cd > 0 ? 1 : 0}|${affordable ? 1 : 0}|${isPending ? 1 : 0}`;
      if (this._stateCache.get(skill.id) !== stateKey) {
        this._stateCache.set(skill.id, stateKey);
        btn.classList.toggle('cooling', cd > 0);
        btn.classList.toggle('unaffordable', !affordable && cd <= 0);
        btn.classList.toggle('active', isPending);
      }

      // ---- 冷却秒数：只在整数秒变化时写文本 ----
      const cdSec = cd > 0 ? Math.ceil(cd) : 0;
      if (this._cdSecCache.get(skill.id) !== cdSec) {
        this._cdSecCache.set(skill.id, cdSec);
        const cdEl = btn.querySelector('.skill-cd');
        if (cdEl) cdEl.textContent = cdSec > 0 ? String(cdSec) : '';
      }

      // ---- 冷却填充：只在整数百分比变化时写样式 ----
      const pct = cd > 0 ? Math.round(sys.cooldownRatio(skill.id) * 100) : 100;
      if (this._cdPctCache.get(skill.id) !== pct) {
        this._cdPctCache.set(skill.id, pct);
        btn.style.setProperty('--cd', `${pct}%`);
      }
    }
  }

  /* ===================================================
   * 内部
   * =================================================== */

  _renderEnergy() {
    const sys = this.system;
    const { pips, energyText } = this._els;
    if (!sys) return;

    if (pips) {
      pips.innerHTML = '';
      for (let i = 0; i < sys.maxEnergy; i++) {
        const pip = document.createElement('span');
        pip.className = i < sys.energy ? 'energy-pip on' : 'energy-pip';
        pips.appendChild(pip);
      }
    }

    if (energyText) {
      energyText.textContent = `${sys.energy}/${sys.maxEnergy}`;
    }
  }
}
